import {gsap} from "gsap";
import {BitmapText, Sprite} from "pixi.js";
import {FireInTheHole2} from "../FireInTheHole2";
import {FITH2Helper} from "../utils/FITH2Helper";
import {FITH2GameConfig} from "../configs/FITH2GameConfig";
import Spine = PIXI.spine.Spine;
import {ArrayUtils} from "../utils/ArrayUtils";
import {FITH2AnimationConfig} from "../configs/FITH2AnimationConfig";
import {TimelineSprite} from "@nolimit/slot-launcher/bin/animation/TimelineSprite";
import {FITH2FontConfig} from "../configs/FITH2FontConfig";
import {ScatterCounter} from "../view/ScatterCounter";
import {ArrayHelper} from "@nolimit/slot-game/bin/utils/ArrayHelper";
import {SpineTween} from "@nolimit/slot-game/bin/animation/SpineTween";

/*
* W Wild
* M1,M2,M3,M4,M5,L1,L2,L3,L4,L5
* S Scatter
* X xSplit
* D dirt, DD Damaged Dirt
* DW, DS, DX D+W, D+S, D+xSplit
* */
export interface ISymbolData {
    symName:string,
    reelSize:number,
    symSize:number,
    id:number
    reelId:number
}

export interface ISymbolShakeConfig {
    x:number,
    y:number,
    duration:number
}

export enum FITH2SymbolStates {
    INIT,
    NORMAL,
    BLUR,
    WIN,
    NO_WIN,
    LANDING,
}

export class FITH2Symbol extends PIXI.Container {
    public static readonly SPECIAL_SYMBOLS:string[] = ["X", "S", "W"];

    protected _id:number = -1;
    protected _name:string;
    protected _reelId:number = -1;
    protected _state:FITH2SymbolStates;
    protected _isRemoved:boolean = false;
    protected _isWinSymbol:boolean = false;
    protected _currentStateSprite:Sprite;
    private _spineName:string;
    protected _spine:Spine | null;

    private _dirtCover:Sprite;
    private _splitCover:Sprite;
    private _textureName:string;
    private _symHeight:number;
    private _symWidth:number;
    private _reelSize:number;
    private _symSize:number;

    private _spent:boolean;
    private _isSpecialSymbol:boolean;
    private _isWild:boolean;
    private _isBehindDirt:boolean;
    private _isBehindBar:boolean;
    private _symbolData:ISymbolData;
    protected _multiplier:BitmapText;
    protected readonly frames: PIXI.Sprite[] = [];

    private isSpecialSymbol():boolean {
        const symbolName:string = this._name;
        return FITH2Symbol.SPECIAL_SYMBOLS.indexOf(symbolName) > -1;
    }

    public getTextureName(symbolName:string):string {
        return `${symbolName}/${symbolName}`;
    }

    private addDirtCover():void {
        if(!this._dirtCover) {
            this._dirtCover = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("D/D")[0]);
            this._dirtCover.anchor.set(0.5, 0.5);
            this._dirtCover.name = "dirtCover";
        }

        this.addChild(this._dirtCover);
    }

    private addTop(): void {
        this.frames[0].name = "D_Top";
        this.frames[0].texture = FireInTheHole2.assetLoader.getTextures("D/D_Top")[0];
        this.addChild(this.frames[0]);
    }

    private addBottom(): void {
        this.frames[1].name = "D_Bottom";
        this.frames[1].texture = FireInTheHole2.assetLoader.getTextures("D/D_Bottom")[0];
        this.addChild(this.frames[1]);
    }

    private addLeft(): void {
        this.frames[2].name = "D_Left";
        this.frames[2].texture = FireInTheHole2.assetLoader.getTextures("D/D_Left")[0];
        this.addChild(this.frames[2]);
    }

    private addRight(): void {
        this.frames[3].name = "D_Right";
        this.frames[3].texture = FireInTheHole2.assetLoader.getTextures("D/D_Right")[0];
        this.addChild(this.frames[3]);
    }

    public addFrames(isBlur: boolean = false,fromExplosion: boolean = false): void {
        if (isBlur || !(this.symName=== "D" || this.isBehindDirt)) {
            return;
        }
        const barId: number = FireInTheHole2.reelTopBar ? FireInTheHole2.reelTopBar.barId : (FireInTheHole2.model.data.totalSpinsPlayed - 1),
        reelSet:string[][] = fromExplosion?FireInTheHole2.model.data.removedSymbols:FireInTheHole2.model.reels;
        //top, bottom,left,right
        if (this._id === 5 || this._id === (2 + barId)) {
            this.addBottom();
        } else if (reelSet[this._reelId][this._id + 1][0] != "D") {
            this.addBottom();
        }

        if (this._id === 0 || this._id === (3 + barId)) {
            this.addTop();
        } else if (reelSet[this._reelId][this._id - 1][0] != "D") {
            this.addTop();
        }

        if (this._reelId === 0) {
            this.addLeft();
        } else if (reelSet[this._reelId - 1][this._id][0] != "D") {
            this.addLeft();
        }

        if (this._reelId === 5) {
            this.addRight();
        } else if (reelSet[this._reelId + 1][this._id][0] != "D") {
            this.addRight();
        }

    }

    private getSpineName(symSize:number):string {
        if(this._isBehindDirt) {
            return `${this._name.slice(1)}`;
        }
        if(this._isSpecialSymbol) {
            return `${this._name}`;
        } else if(this._name[0] === "M") {
            return `SymM`;
        } else if(this._name[0] === "L") {
            return `SymL`;
        }
        return ``;
    }

    protected getSymHeight():number {
        return (FITH2GameConfig.reelHeight / this._reelSize);
    }

    protected addSpine():void {
        if(this._isBehindDirt) {
            this.addDirtCover();
        }
        this.addChild(this._currentStateSprite);
    }

    public playLandingAnimation():gsap.core.Timeline {
        return gsap.timeline().add([
            gsap.to(this._currentStateSprite.scale, 0.1, {x : 0.95, y : 0.95}),
            gsap.to(this._currentStateSprite.scale, 0.3, {x : 1, y : 1, ease : "sine.in", delay : 0.1})
        ]);
    }

    public getStateAnimation(state:FITH2SymbolStates = FITH2SymbolStates.NORMAL, duration:number = 0):gsap.core.Timeline {
        if(FITH2Helper.isInvisibleSym(this.symName)) {
            return gsap.timeline();
        }
        if(this._state === state) {
            return gsap.timeline();
        }

        const isBlur:boolean = (state === FITH2SymbolStates.BLUR),
            tl = gsap.timeline();
        if(!isBlur){
            this.addSpine();
        }
        tl.add(() => {
            if(this._isBehindDirt) {
                this._dirtCover.texture = FireInTheHole2.assetLoader.getTextures(`D/D${isBlur ? "_spin" : ""}`)[0];
                this._dirtCover.name = `D/D${isBlur ? "_spin" : "D"}`;
                this._dirtCover.scale.set(isBlur ? 2 : 1);
                this._currentStateSprite.texture = FireInTheHole2.assetLoader.getTextures(`${this._name}/D${this._name}`)[0];
                this._currentStateSprite.name = `currentStateSprite${this._name}/D`;
            } else {
                this._currentStateSprite.texture = FireInTheHole2.assetLoader.getTextures(`${this._name}/${this._name}${isBlur ? "_spin" : ""}`)[0];
                this._currentStateSprite.scale.set(isBlur ? 2 : 1);
            }

            // this._spine?.state.setAnimation(0, isBlur ? "spin" : "base", false);
        }, 0);
        if(this._isSpecialSymbol && (state === FITH2SymbolStates.LANDING)) {
            tl.add(this.playLandingAnimation(), 0);
        }
        this._state = state;
        return tl;
    }

    public init(initState:FITH2SymbolStates):FITH2Symbol {
        if(FITH2Helper.isInvisibleSym(this.symName)) {
            return this;
        }

        this.getStateAnimation(initState);
        return this;
    }

    public playInitialWin(winType:string):gsap.core.Timeline {
        if(this._isBehindDirt || this._isBehindBar) {
            return gsap.timeline();
        }
        this._isWinSymbol = true;
        const tl:gsap.core.Timeline = gsap.timeline(),
            fs:number = 0.033,
            screenSymbol:PIXI.Sprite = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("WinEff_A/WinEff_A_")[0]),
            winEffect:TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures("WinEff_A/WinEff_A_"));
        screenSymbol.alpha = 0;
        if(!this._isSpecialSymbol) {
            this.parent.removeChild(this);
            FireInTheHole2.reelArea.allReels[this.reelId].reelView.initialWinSymbolView.addChild(this);
            //return new SpineTween(this._spine!, 0, `${winType}win_toggle`);
        }

        tl.add([
            () => {
                screenSymbol.blendMode = PIXI.BLEND_MODES.SCREEN;
                screenSymbol.anchor.set(0.5, 0.5);
                this.addChild(screenSymbol);
            },
            gsap.to(this.scale, 3 * fs, {x : 1.15, y : 1.15}),
            gsap.to(this.scale, 2 * fs, {x : 1.1, y : 1.1, delay : 3 * fs}),
            gsap.to(screenSymbol, 5 * fs, {alpha : 0.8}),
            gsap.to(this.scale, 5 * fs, {x : 1, y : 1, delay : 20 * fs}),
            gsap.to(screenSymbol, 5 * fs, {alpha : 0, delay : 20 * fs}),
        ]);
        tl.add([
            () => {
                winEffect.blendMode = PIXI.BLEND_MODES.ADD;
                winEffect.anchor.set(0.5, 0.5);
                this.addChild(winEffect);
            },
            winEffect.getAnimationAutoShowHide()
        ], 5 * fs);
        tl.add(() => {
            this.removeChild(screenSymbol);
            this.removeChild(winEffect);
        });

        return tl;
    }

    private turnToDamagedDirt():gsap.core.Timeline {
        return gsap.timeline().add(() => {
            this._name = "DD";
            this._currentStateSprite.texture = FireInTheHole2.assetLoader.getTextures("D/DD")[0];
        });
    }

    private addSplitEffect():gsap.core.Timeline {
        const tl = gsap.timeline();
        const splitEffect:PIXI.Sprite = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("temp/nudge_light")[0]);
        tl.add([
            gsap.to(splitEffect, 0.4, {alpha : 0}),
            () => {
                splitEffect.blendMode = PIXI.BLEND_MODES.ADD;
                splitEffect.anchor.set(0.5, 0.5);
                this.addChild(splitEffect);
            }
        ], 0);
        tl.add(() => {
            this.removeChild(splitEffect);
        });
        return tl;
    }

    private addExplodeEffect(isAboveMediumWin:boolean):gsap.core.Timeline {
        const tl = gsap.timeline();
        if(this._name[0] === "L") {
            const baseName:string = `broke_wood`,
                winLEffect:TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures(`${baseName}/${baseName}_`));
            tl.add([
                () => {
                    winLEffect.anchor.set(0.5, 0.5);
                    this.addChild(winLEffect);
                },
                winLEffect.getAnimationAutoShowHide()
            ], 0);
            tl.add(() => {
                this.removeChild(winLEffect);
            });
        }

        const baseName:string = `WinEff_${this._name}`,
            baseEffect:TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures(`${baseName}/${baseName}_`));
        tl.add([
            () => {
                baseEffect.blendMode = PIXI.BLEND_MODES.SCREEN;
                baseEffect.anchor.set(0.5, 0.5);
                this.addChild(baseEffect);
            },
            gsap.to(this._currentStateSprite, 0.3, {alpha : this._isRemoved ? 0 : 1}),
            baseEffect.getAnimationAutoShowHide()
        ], 0);
        tl.add(() => {
            this.removeChild(baseEffect);
        });
        if(!(this._isWinSymbol || isAboveMediumWin)){
            return tl;
        }
            const winBig:string = `WinBig_${this._name[0] === "M" ? this._name : "L"}`,
                winBigEffect:TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures(`${winBig}/${winBig}_`));
            tl.add([
                () => {
                    winBigEffect.blendMode = PIXI.BLEND_MODES.SCREEN;
                    winBigEffect.anchor.set(0.5, 0.5);
                    this.addChild(winBigEffect);
                },
                winBigEffect.getAnimationAutoShowHide()
            ], 0);
            tl.add(() => {
                this.removeChild(winBigEffect);
            });

        return tl;
    }

    public addScreenLayer(duration:number ):gsap.core.Timeline {
        const tl = gsap.timeline();
        const size:string = (this._symSize===1?"":`_${this._symSize < 9 ? this._symSize : 8}`),
            screenSymbol:PIXI.Sprite = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures(`${this._name}/${this._name}${size}`)[0]);
        screenSymbol.name = `${this._name}/${this._name}${size}Screen`;
        tl.add([
            () => {
                screenSymbol.blendMode = PIXI.BLEND_MODES.SCREEN;
                screenSymbol.anchor.set(0.5, 0.5);
                this.addChild(screenSymbol);
                if(this._multiplier){
                    this.addChild(this._multiplier);
                }
            }
        ], 0);
        tl.add(() => {
            this.removeChild(screenSymbol);
        },duration);
        return tl;
    }
    public getShakeAnimation(config:ISymbolShakeConfig):gsap.core.Timeline {
        const tl = gsap.timeline();
        if(FITH2Helper.isInvisibleSym(this._name)) {
            return tl;
        }
        const wiggleX = FITH2AnimationConfig.capWinAnticipationWiggleY,
            wiggleY = FITH2AnimationConfig.capWinAnticipationWiggleX;
        const wiggles = [wiggleX, wiggleY];
        const x = `${Math.random() > 0.5 ? "-" : "+"}=${config.x}`;
        const y = `${Math.random() > 0.5 ? "-" : "+"}=${config.y}`;


        if(this._spine) {
            tl.add(gsap.to(this._spine.position, {
                duration : config.duration,
                x : x,
                ease : wiggles[0]
            }));
            tl.add(gsap.to(this._spine.position, {
                duration : config.duration,
                y : y,
                ease : wiggles[1]
            }), "<");
        } else {
            tl.add(gsap.to(this._currentStateSprite.position, {
                duration : config.duration,
                x : x,
                ease : wiggles[0]
            }));
            tl.add(gsap.to(this._currentStateSprite.position, {
                duration : config.duration,
                y : y,
                ease : wiggles[1]
            }), "<");
        }

        return tl.reverse();
    }

    constructor(symbolData:ISymbolData, state:FITH2SymbolStates = FITH2SymbolStates.NORMAL) {
        super();
        let symbolName:string = symbolData.symName;
        this.name = symbolName;
        this._isRemoved = false;
        this._isWinSymbol = false;
        this._state = FITH2SymbolStates.INIT;
        if(symbolName == undefined) {
            debugger;
        }
        this._isBehindDirt = (symbolName !== "D" && symbolName[0] === "D");
        this._isWild = symbolName[0] === "W";
        if(this._isBehindDirt) {
            symbolName = symbolName.slice(1)
        }
        this._name = symbolName;
        this._textureName = symbolName;
        this._isSpecialSymbol = this.isSpecialSymbol();

        if(!FITH2Helper.isInvisibleSym(symbolName)) {
            this._currentStateSprite = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures(this.getTextureName(symbolName))[0]);
            this._currentStateSprite.name = "currentStateSprite";
            this._currentStateSprite.anchor.set(0.5, 0.5);
            this._currentStateSprite.position.set(0, 0);
        }

        this._spineName = this.getSpineName(1);
        this._id = symbolData.id;
        this._reelId = symbolData.reelId;
        this._symSize = symbolData.symSize;
        this._spent = false;
        if(this._symSize > 1) {
            this.split(this._symSize);
        }
        this._symWidth = FITH2GameConfig.symbolWidth;
        this._symHeight = this.getSymHeight();
        this.init(state);
        this._symbolData = symbolData;
        this.frames = ArrayHelper.initArrayWithValues(4, () => {
            const frame: PIXI.Sprite = new PIXI.Sprite();
            frame.anchor.set(0.5, 0.5);
            return frame;
        });
        this.addFrames();
    }

    public explodeDirt(): gsap.core.Timeline {
        const tl = gsap.timeline(),
            explodeEffect: TimelineSprite = new TimelineSprite(FireInTheHole2.assetLoader.getTextures("D_break/D_break_"));
        tl.add([
            () => {
                explodeEffect.anchor.set(0.5, 0.5);
                this.addChild(explodeEffect);
            },
            explodeEffect.getAnimationAutoShowHide()
        ], 0);
        tl.add(() => {
            this.removeChild(explodeEffect);
        });
        return tl;
    }

    protected removeDirt(fromSplit:boolean = true):gsap.core.Timeline {
        this._isBehindDirt = false;
        const tl = gsap.timeline();
        tl.add([
            ()=>{
                this._currentStateSprite.texture = FireInTheHole2.assetLoader.getTextures(`${this._name}/${this._name}`)[0];
                this.addChild(this._dirtCover);
            },
            gsap.to(this._dirtCover, 0.2, {alpha : 0})
        ], 0);
        this.frames.forEach((frame: PIXI.Sprite) => {
            tl.add(gsap.to(frame, 0.2, {alpha: 0}), 0);
        });
        if(fromSplit) {
            tl.add(this.addSplitEffect(), 0)
        }else{
            tl.add(this.explodeDirt(), 0)
        }
        tl.add(() => {
            this._dirtCover.visible = false;
        });

        return tl;
    }

    private updateMultiplier(size:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            if(!this._multiplier) {
                this._multiplier = FITH2Helper.createBitmapText("", FITH2FontConfig.MULTIPLIER_SYMBOL_STYLE, new PIXI.Point(1, 0));
                this._multiplier.position.set(50, -50);
            }
            this.addChild(this._multiplier);
            this._multiplier.text = `x${size}`;
        });
        return tl;
    }

    public split(size:number = -1):gsap.core.Timeline {
        const tl = gsap.timeline();

        if(this._isBehindDirt) {
            return this.removeDirt();
        }

        if(this._name === "S" || this._name === "DD" || this._spent || this._name === "X") {
            return tl;
        }

        tl.add(this.addSplitEffect(), 0);
        if(this._name === "D") {
            tl.add(this.turnToDamagedDirt())
            return tl;
        }

        size = size > 0 ? size : this._symSize * 2;

        if(this._symSize === size) {
            return gsap.timeline();
        }
        this._symSize = size;
        if(this._name === "W") {
            if(this._spine) {

                tl.add([
                    ()=>{
                        this.parent.removeChild(this);
                        FireInTheHole2.reelArea.allReels[this.reelId].reelView.scatterWinView.addChild(this);
                    },
                    new SpineTween(this._spine!, 0, "split")
                ],0);
            }
            tl.add(this.updateMultiplier(size), 0.2);
            return tl;
        }
        tl.add(gsap.to(this._currentStateSprite, 0.2, {alpha : 0.2}), 0);
        tl.add([
            gsap.to(this._currentStateSprite, 0.2, {alpha : 1}),
            () => {
                if(!FITH2Helper.isLargerSymbol(this._textureName)) {
                    this._currentStateSprite.scale.set(1);
                    this._currentStateSprite.texture = FireInTheHole2.assetLoader.getTextures(`${this._name}/${this._name}_${size < 9 ? size : 8}`)[0];

                    if(size > 7) {
                        if(!this._multiplier) {
                            this._multiplier = FITH2Helper.createBitmapText("", FITH2FontConfig.MULTIPLIER_SYMBOL_STYLE, new PIXI.Point(1, 0));
                            this._multiplier.position.set(50, -50);
                        }
                        this.addChild(this._multiplier);
                        this._multiplier.text = `x${size}`;
                    }
                }
            }], "-=0.2");

        return tl;
    }

    public explode(winType:string, barId:number = -1):gsap.core.Timeline {
        if(this._isRemoved || this._name === "BA") {
            return gsap.timeline();
        }

        if(this._isBehindDirt) {
            return this.removeDirt(false);
        }

        if(this._name === "S") {
            return gsap.timeline();
        }
        const tl = gsap.timeline(),
            fs:number = 0.033;
        this._isRemoved = true;

        if(this._multiplier) {
            tl.add(gsap.to(this._multiplier, 7 * fs, {alpha : 0, delay : 5 * fs}), 0);
        }
        tl.add([
            gsap.to(this._currentStateSprite, 7 * fs, {alpha : 0, delay : (this._isWinSymbol?25:0) * fs}),
            gsap.to(this._currentStateSprite.scale, 7 * fs, {x : 0.5, y : 0.5, delay : (this._isWinSymbol?25:0) * fs}),
            this.addExplodeEffect(winType.length === 0)
        ], 0);
        tl.add(() => {
            if(this._isRemoved) {
                this.remove();
            }
        });
        return tl;
    }

    public afterWildMining():void {
        this._isRemoved = true;
    }

    public remove():FITH2Symbol {
        if(this._dirtCover) {
            this.removeChild(this._dirtCover);
        }

        if(this._splitCover) {
            this.removeChild(this._splitCover);
        }

        if(this._currentStateSprite) {
            this.removeChild(this._currentStateSprite);
        }
        if(this.parent) {
            this.parent.removeChild(this);
        }

        this.frames.forEach((frame: PIXI.Sprite) => {
            this.removeChild(frame);
        });

        this.destroy({children : true});
        this._isRemoved = true;
        return this;
    }

    public get isRemoved():boolean {
        return this._isRemoved;
    }

    public set isRemoved(value:boolean) {
        this._isRemoved = value;
    }

    public get symName():string {
        return this._name;
    }

    public get id():number {
        return this._id;
    }

    public get reelId():number {
        return this._reelId;
    }

    public set id(value:number) {
        this._id = value;
    }

    public get symHeight():number {
        return this._symHeight;
    }

    public get isBehindDirt():boolean {
        return this._isBehindDirt;
    }

    public set isBehindBar(value:boolean) {
        this._isBehindBar = value;
    }

    public get isBehindBar():boolean {
        return this._isBehindBar;
    }

    public get isWild():boolean {
        return this._isWild;
    }

    public get symSize():number {
        return this._symSize;
    }

    public get isWinSymbol():boolean {
        return this._isWinSymbol;
    }
}